// 🧠 SNAP CHART AI OPENAI SERVICE - LOVABLE CLOUD
// Uses Edge Function exclusively (backend API key)

import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

class OpenAIService {
  constructor() {
    this.environment = this.detectEnvironment();
    
    console.log('🚀 OpenAI Service initialized (Edge Function mode):', {
      environment: this.environment,
      timestamp: new Date().toISOString()
    });
  }

  detectEnvironment() {
    const hostname = window.location.hostname;
    if (hostname.includes('lovableproject.com') || hostname.includes('lovable.app')) return 'Lovable';
    if (hostname.includes('webcontainer')) return 'WebContainer';
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'Development';
    return 'Production';
  }

  // Get the current user's JWT token for authenticated edge function calls
  async getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        console.log('🔑 Using authenticated user token for AI request');
      } else {
        // Fallback to anon key if no session
        headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
        console.log('⚠️ No user session, using anon key');
      }
    } catch (e) {
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
      console.log('⚠️ Could not get session, using anon key');
    }

    return headers;
  }

  // ✅ EDGE FUNCTION CHAT COMPLETION (USES BACKEND API KEY)
  async createChatCompletion(model, messages, options = {}) {
    console.log('🚀 Creating chat completion via Edge Function:', { model, messagesCount: messages?.length });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

      const headers = await this.getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/openai-chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: model || 'gpt-5.5',
          messages,
          max_tokens: options.max_tokens || 4000,
          temperature: options.temperature || 0.7
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Service error' }));
        console.error('❌ Edge Function error:', response.status, errorData);
        
        if (response.status === 500 && errorData.error?.includes('not configured')) {
          throw new Error('❌ Configuration backend en cours. Réessayez dans quelques instants.');
        }
        
        throw new Error(`❌ Erreur service AI: ${errorData.error || 'Erreur serveur'}`);
      }
      
      console.log('✅ Edge Function response received');
      return await response.json();
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('❌ Délai dépassé. Le service AI est surchargé, réessayez.');
      }
      throw error;
    }
  }

  // ✅ VISION ANALYSIS (USES BACKEND API KEY)
  async analyzeWithVision(prompt, images, options = {}) {
    console.log('🖼️ Analyzing with Vision via Edge Function:', { imagesCount: images?.length || 0 });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout for vision (Gemini can take longer with multiple images)

      const headers = await this.getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/openai-vision`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          images,
          model: options.model || 'gpt-5.5',
          max_tokens: options.max_tokens || 8000,
          systemPrompt: options.systemPrompt
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Service error' }));
        console.error('❌ Edge Vision Function error:', response.status, errorData);
        
        if (response.status === 500 && errorData.error?.includes('not configured')) {
          throw new Error('❌ Configuration backend en cours. Réessayez dans quelques instants.');
        }
        
        throw new Error(`❌ Erreur analyse AI: ${errorData.error || 'Erreur serveur'}`);
      }
      
      const data = await response.json();
      console.log('✅ Edge Function Vision response received');
      
      return {
        choices: [{ message: { content: data.content } }],
        usage: data.usage,
        model: data.model
      };
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('❌ Délai dépassé. Réessayez avec moins d\'images ou réessayez plus tard.');
      }
      throw error;
    }
  }
}

// ✅ CREATE SERVICE INSTANCE
const openaiService = new OpenAIService();

// ✅ EXPORT MAIN FUNCTION
export const createChatCompletion = async (model, messages, options) => {
  return openaiService.createChatCompletion(model, messages, options);
};

// ✅ ANALYZE WITH OPENAI - SUPPORTS TEXT AND IMAGES
export const analyzeWithOpenAI = async (prompt, model = null, options = {}) => {
  const selectedModel = model || 'gpt-5.5';

  console.log('🤖 analyzeWithOpenAI using model:', selectedModel);
  
  if (options.imageBase64) {
    const images = Array.isArray(options.imageBase64) ? options.imageBase64 : [options.imageBase64];
    console.log(`📸 Vision mode - ${images.length} chart image(s)`);
    
    return openaiService.analyzeWithVision(prompt, images, {
      model: selectedModel,
      max_tokens: options.max_tokens || 8000,
      systemPrompt: options.systemPrompt
    });
  }
  
  const messages = [{ role: 'user', content: prompt }];
  return openaiService.createChatCompletion(selectedModel, messages, options);
};

export { OpenAIService };
export default openaiService;
